import React, { useState } from 'react';
import { Button, IconButton, Typography, Box, Tooltip } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  StreamSessionStatus,
  terminateStreamSession,
} from 'src/services/streamingService';
import { format } from 'date-fns';
import ModalDialog from 'src/components/ModalDialog';
import DataTable, { Column, CellProps, Row } from 'src/components/DataTable';
import Badge from 'src/components/Badge';
import { useStreamStart } from 'src/hooks/useStreamStart';

// Interface for session data - must extend Row for DataTable
export interface SessionData extends Row {
  id: string;
  appId: string;
  userId: string;
  userName: string;
  status: StreamSessionStatus;
  startTime: string; // Convert Date to string for DataTable compatibility
  duration: string;
  [key: string]: any; // Index signature required for Row type
}

// Calculate duration from start and end time
function calculateDuration(startTime: Date, endTime?: Date): string {
  const end = endTime || new Date();
  const diffMs = end.getTime() - startTime.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 60) {
    return `${diffMins}m`;
  }

  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return `${hours}h ${mins}m`;
}

// Mock active sessions data based on the provided app
const mockActiveSessions: SessionData[] = [
  {
    id: 'session-001',
    appId: 'f649ccf5-6819-48ae-a8a3-252781d24bf7', // Using the app ID
    userId: 'usr-001',
    userName: 'John Smith',
    status: StreamSessionStatus.ACTIVE,
    startTime: format(
      new Date(new Date().getTime() - 45 * 60000),
      'MMM dd, yyyy HH:mm'
    ), // 45 minutes ago
    duration: calculateDuration(new Date(new Date().getTime() - 45 * 60000)),
  },
  {
    id: 'session-002',
    appId: 'f649ccf5-6819-48ae-a8a3-252781d24bf7', // Using the app ID
    userId: 'usr-002',
    userName: 'Sarah Johnson',
    status: StreamSessionStatus.ACTIVE,
    startTime: format(
      new Date(new Date().getTime() - 120 * 60000),
      'MMM dd, yyyy HH:mm'
    ), // 2 hours ago
    duration: calculateDuration(new Date(new Date().getTime() - 120 * 60000)),
  },
];

interface ActiveSessionsDialogProps {
  open: boolean;
  onClose: () => void;
  app: any;
  appName: string;
  solutionId?: string;
  siteId?: string;
  twinId?: string;
}

export default function ActiveSessionsDialog({
  open,
  onClose,
  app,
  appName,
  solutionId,
  siteId,
  twinId,
}: ActiveSessionsDialogProps) {
  const [terminatingSessionId, setTerminatingSessionId] = useState<
    string | null
  >(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Extract appId from app object
  const appId =
    (Array.isArray(app) && app.length > 0 ? app[0]?.id : app?.id) || '';

  // Initialize useStreamStart hook with required parameters
  const streamStart = useStreamStart({
    appId,
    onSuccess: (session) => {
      // Navigate to the streaming session in a new tab
      window.open(`/app/${appId}/sessions/${session.id}`, '_blank');
      onClose();
    },
  });

  // Show all mock sessions if an app is available
  const appSessions =
    (Array.isArray(app) && app.length > 0) || app ? mockActiveSessions : [];

  // Create a new session using the API
  const handleCreateNewSession = async () => {
    if (!solutionId || !siteId || !twinId || !appId) {
      console.error('Missing required parameters for session creation:', {
        solutionId,
        siteId,
        twinId,
        appId,
      });
      return;
    }

    setIsCreatingSession(true);
    try {
      await streamStart.mutateAsync();
    } catch (error) {
      console.error('Failed to create new session:', error);
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Join an existing session by opening in a new tab
  const handleJoinSession = (sessionId: string) => {
    // Use the session's appId directly for joining
    window.open(`/app/${sessionId}/sessions/${sessionId}`, '_blank');
    onClose();
  };

  // Terminate a session
  const handleTerminateSession = async (sessionId: string) => {
    setTerminatingSessionId(sessionId);
    try {
      await terminateStreamSession(sessionId);
      // In a real app, you would update the sessions list
      // For now we'll just simulate it with a delay
      setTimeout(() => {
        setTerminatingSessionId(null);
        // In real app: refetchSessions();
      }, 1000);
    } catch (error) {
      console.error('Failed to terminate session:', error);
      setTerminatingSessionId(null);
    }
  };

  // Define the session ID column with custom rendering
  const SessionIdCell = ({ value }: CellProps<any, SessionData>) => (
    <Typography variant="body2" fontFamily="monospace">
      {value as string}
    </Typography>
  );

  // Define the status column with the Badge component used elsewhere in the app
  const StatusCell = ({ value }: CellProps<any, SessionData>) => {
    // Map the stream status to appropriate badge properties
    const statusProps = {
      label: value as string,
      color:
        value === StreamSessionStatus.ACTIVE ? 'success' : ('default' as any),
    };

    return <Badge {...statusProps} />;
  };

  // Define the actions column
  const ActionsCell = ({ item }: CellProps<any, SessionData>) => {
    if (!item) return null;

    return (
      <Box>
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => handleTerminateSession(item.id)}
          disabled={terminatingSessionId === item.id}
          startIcon={<CancelIcon fontSize="small" />}
        >
          Terminate
        </Button>
      </Box>
    );
  };

  // Define columns for the DataTable
  const columns: Column<SessionData>[] = [
    {
      id: 'id',
      title: 'Session ID',
      Component: SessionIdCell,
    },
    {
      id: 'userName',
      title: 'User',
    },
    {
      id: 'status',
      title: 'Status',
      Component: StatusCell,
    },
    {
      id: 'startTime',
      title: 'Start Time',
    },
    {
      id: 'duration',
      title: 'Duration',
    },
    {
      id: 'actions',
      title: 'Actions',
      Component: ActionsCell,
    },
  ];

  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      title={`${appName} - Active Sessions`}
      cancelBtnText="Cancel"
      confirmBtnText="New Session"
      onCancel={onClose}
      onConfirm={handleCreateNewSession}
      disabled={isCreatingSession || streamStart.isLoading}
      maxWidth="md"
      fullWidth
    >
      {appSessions.length === 0 ? (
        <Typography variant="body1" sx={{ py: 2, fontFamily: 'sans-serif' }}>
          No active sessions found for this application.
        </Typography>
      ) : (
        <DataTable data={appSessions} columns={columns} />
      )}

      {streamStart.error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          Failed to create session: {streamStart.error.message}
        </Typography>
      )}
    </ModalDialog>
  );
}
